/**
 * TimelineContainer Component
 *
 * Root layout container for timeline.
 * Uses CSS Grid for header/content layout with fixed header row.
 */

import React from 'react'
import { Box, LinearProgress, useTheme } from '@mui/material'
import { X_AXIS_HEIGHT } from '../timeline-constants'
import { TimelineYAxis } from './timeline-y-axis'
import { TimelineHeader } from './timeline-header'
import { TimelineContent } from './timeline-content'
import { TimelineGradientMask } from './timeline-gradient-mask'
import { TimelineControls } from './timeline-controls'
import { useFeatures } from '~/common/use-features'

/**
 * TimelineContainer - Root layout container
 *
 * Grid Layout:
 * - Row 1 (fixed): X-axis header (70px)
 * - Row 2 (flexible): Content area with scroll
 *
 * Overlays (absolute positioned):
 * - Y-axis labels (left, scrolls with content)
 * - Gradient mask (left, improves label readability)
 * - Controls (bottom center)
 *
 * @param {Object} props
 * @param {number} props.contentHeight - Fixed content height (optional)
 * @param {boolean} props.hideYAxis - Hide Y-axis labels
 * @param {Array} props.groups - Grouped crops
 * @param {Array} props.expandedGroupIds - Expanded group IDs
 * @param {Function} props.toggleGroup - Group toggle handler
 * @param {Array} props.crops - Transformed crop data
 * @param {boolean} props.interactive - Enable phase drag editing
 * @param {Function} props.onPhaseAdjust - Phase adjustment callback
 * @param {Function} props.onTaskAdjust - Task adjustment callback
 * @param {boolean} props.forceAllExpand - Force all crops expanded
 * @param {boolean} props.hideRowLines - Hide horizontal grid lines
 * @param {boolean} props.hideToggleButtons - Hide controls panel
 * @param {boolean} props.fullscreen - Remove border and border radius for fullscreen view
 * @param {React.ReactNode} props.children - Custom overlay components
 */
export const TimelineContainer = ({
  contentHeight,
  hideYAxis,
  groups,
  expandedGroupIds,
  toggleGroup,
  crops,
  interactive,
  onPhaseAdjust,
  onTaskAdjust,
  forceAllExpand,
  hideRowLines,
  hideToggleButtons,
  fullscreen = false,
  loading = false,
  panAnimationX = 0,
  hideBadges = false,
  printMode = false,
  children,
}) => {
  const theme = useTheme()
  const { features } = useFeatures()

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: '1fr',
        gridTemplateRows: contentHeight
          ? `${X_AXIS_HEIGHT}px ${contentHeight}px`
          : printMode
          ? `${X_AXIS_HEIGHT}px auto`
          : `${X_AXIS_HEIGHT}px 1fr`,
        ...(!fullscreen && !printMode ? {
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1.5,
        } : {}),
        overflow: printMode ? 'visible' : 'hidden',
        height: printMode ? 'auto' : '100%',
      }}
    >
      {/* Y-axis labels (absolute positioned overlay) */}
      <TimelineYAxis
        groups={groups}
        hideYAxis={hideYAxis}
        expandedGroupIds={expandedGroupIds}
        toggleGroup={toggleGroup}
      />

      {/* X-axis header (grid row 1) */}
      <TimelineHeader />

      {/* Loading bar — absolute overlay just below header. */}
      {loading && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: X_AXIS_HEIGHT,
            left: 0,
            right: 0,
            zIndex: 25,
          }}
        />
      )}

      {/* Main content (grid row 2) */}
      <TimelineContent
        crops={crops}
        interactive={interactive}
        onPhaseAdjust={onPhaseAdjust}
        onTaskAdjust={onTaskAdjust}
        forceAllExpand={forceAllExpand}
        hideRowLines={hideRowLines}
        panAnimationX={panAnimationX}
        hideBadges={hideBadges}
      />

      {/* Left gradient mask (absolute positioned overlay) */}
      {!hideYAxis && !printMode && <TimelineGradientMask />}

      {/* Custom overlay children (e.g., row helpers) */}
      {!printMode && children}

      {/* Controls (absolute positioned at bottom center) */}
      {features['timeline_controls'] && !printMode && (
        <TimelineControls hideToggleButtons={hideToggleButtons} />
      )}
    </Box>
  )
}
